import { Alert, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import React, { useState } from 'react';
import type { CallLine, CapitalCall } from '../../data/types';
import {
  DEFAULT_INTEREST_RATE,
  DEFAULT_NOTICE_CURE_DAYS,
  daysOverdue,
  defaultInterest,
} from '../../domain/lifecycleCalc';
import { addDays, date, DEMO_TODAY, money } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { ConfirmDialog, useToast } from '../../ui/feedback';
import { SectionCard, StatusChip } from '../../ui/primitives';

const REMEDIES = [
  'Set off the unpaid amount and interest against future distributions',
  'Forfeit the unpaid commitment; paid-up units remain',
  'Compulsory sale of units at 75% of NAV under the trust deed',
];

const STAGE_LABEL: Record<string, string> = {
  NOTICE_ISSUED: 'Default notice issued',
  REMEDY_PROPOSED: 'Remedy awaiting approval',
  REMEDIED: 'Remedy applied',
};

export default function CallDefaultsPanel({ call }: { call: CapitalCall }) {
  const scope = useTenantScope();
  const { dispatch } = useRegistry();
  const toast = useToast();
  const [remedyFor, setRemedyFor] = useState<CallLine | null>(null);
  const [remedy, setRemedy] = useState(REMEDIES[0]);
  const overdue = daysOverdue(call.dueDate, DEMO_TODAY);
  const unpaid = call.lines.filter((line) => line.status !== 'PAID');
  if (overdue === 0 || unpaid.length === 0) return null;

  return (
    <SectionCard
      title={`Defaults · ${overdue} days overdue`}
      subtitle={`Default interest at ${DEFAULT_INTEREST_RATE * 100}% p.a. from the due date; cure period ${DEFAULT_NOTICE_CURE_DAYS} days after a default notice (illustrative trust deed terms)`}
      noPadding
    >
      <Alert severity="warning" sx={{ mx: 2.5, mb: 1.5 }}>
        {unpaid.length} holders haven&apos;t paid in full. Issue default notices, waive interest
        where agreed, and propose a remedy once the cure period ends.
      </Alert>
      <DataTable
        rows={unpaid}
        rowKey={(row) => row.holderId}
        dense
        columns={[
          {
            key: 'holder',
            label: 'Holder',
            render: (row) => scope.holderById.get(row.holderId)?.name,
          },
          {
            key: 'out',
            label: 'Outstanding',
            align: 'right',
            render: (row) => money(row.amount - row.paid),
          },
          {
            key: 'interest',
            label: 'Default interest',
            align: 'right',
            render: (row) =>
              row.interestWaived
                ? 'Waived'
                : money(defaultInterest(row, call.dueDate, DEMO_TODAY), true),
          },
          {
            key: 'stage',
            label: 'Stage',
            render: (row) => (
              <>
                <StatusChip
                  status={
                    row.defaultStage === 'REMEDIED'
                      ? 'COMPLETED'
                      : row.defaultStage
                        ? 'PENDING'
                        : 'OVERDUE'
                  }
                  label={row.defaultStage ? STAGE_LABEL[row.defaultStage] : 'Overdue'}
                />
                {row.noticeDate && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    Cure period ends {date(addDays(row.noticeDate, DEFAULT_NOTICE_CURE_DAYS))}
                  </Typography>
                )}
              </>
            ),
          },
          {
            key: 'actions',
            label: '',
            align: 'right',
            render: (row) => (
              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                {!row.defaultStage && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      dispatch({
                        type: 'CALL_DEFAULT_NOTICE',
                        callId: call.id,
                        holderId: row.holderId,
                      });
                      toast('Default notice issued by email, app and post');
                    }}
                  >
                    Issue notice
                  </Button>
                )}
                {!row.interestWaived && row.defaultStage !== 'REMEDIED' && (
                  <Button
                    size="small"
                    onClick={() => {
                      dispatch({
                        type: 'CALL_DEFAULT_WAIVE',
                        callId: call.id,
                        holderId: row.holderId,
                      });
                      toast('Default interest waived (audited)');
                    }}
                  >
                    Waive interest
                  </Button>
                )}
                {row.defaultStage === 'NOTICE_ISSUED' && (
                  <Button size="small" color="error" onClick={() => setRemedyFor(row)}>
                    Propose remedy
                  </Button>
                )}
              </Stack>
            ),
          },
        ]}
      />
      <ConfirmDialog
        open={Boolean(remedyFor)}
        title="Propose a default remedy"
        confirmLabel="Send for approval"
        danger
        onClose={() => setRemedyFor(null)}
        onConfirm={() => {
          if (remedyFor)
            dispatch({
              type: 'CALL_DEFAULT_REMEDY',
              callId: call.id,
              holderId: remedyFor.holderId,
              remedy,
            });
          toast('Remedy sent to Approvals');
          setRemedyFor(null);
        }}
        body={
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {remedyFor ? scope.holderById.get(remedyFor.holderId)?.name : ''} has{' '}
              {money(remedyFor ? remedyFor.amount - remedyFor.paid : 0)} unpaid. Remedies follow the
              trust deed and need approval.
            </Typography>
            <TextField
              select
              fullWidth
              label="Remedy"
              value={remedy}
              onChange={(event) => setRemedy(event.target.value)}
            >
              {REMEDIES.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
          </>
        }
      />
    </SectionCard>
  );
}
